import React, { useEffect, useMemo, useState } from 'react';
import {
  updateLscArchiveAutomationPolicy,
  LscApiError,
  type UpdateLscArchiveAutomationPolicyPayload,
} from '../api';
import { ARCHIVE_AUTOMATION_MODE_OPTIONS, isAutoApproveDisabled, isAutoPublishDisabled } from '../archiveAutomationPolicy';
import type { LscArchiveAutomationPolicy, LscLeague } from '../types';
import ErrorBanner from './ErrorBanner';

const VISIBILITY_OPTIONS = [
  { value: 'league_members', label: 'League members' },
  { value: 'commissioner_only', label: 'Commissioner only until reviewed' },
] as const;

const DELAY_OPTIONS = [
  { value: 0, label: 'Immediately' },
  { value: 10, label: '10 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 1440, label: 'Next morning digest' },
] as const;

const DEFAULT_MIN_AUTO_ARCHIVE_SCORE = 90;

// Dark typed text on a light background so commissioner entries stay readable
// on the dark Archive Automation surface.
const FIELD_CLASS =
  'w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-secondary-900 ' +
  'placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

const CONFIDENCE_BANDS = [
  { range: '90–100', label: 'Strong match', note: 'Eligible for auto-archive if no exceptions' },
  { range: '75–89', label: 'Needs review', note: 'Sent to Needs Attention' },
  { range: '60–74', label: 'Weak match', note: 'Sent to Needs Attention' },
  { range: 'Below 60', label: 'Not enough evidence', note: 'Never auto-archived' },
] as const;

const REVIEW_TOGGLES = [
  ['require_review_for_low_confidence', 'Review unclear games'],
  ['require_review_for_side_conflict', 'Review matchup conflicts'],
  ['require_review_for_restart_continuation', 'Review restarted or multi-part streams'],
  ['require_review_for_empty_hud', 'Review unclear on-screen team names'],
  ['require_review_for_manual_assignment', 'Require review for unclear matchup'],
] as const;

interface ArchiveAutomationPanelProps {
  league: LscLeague;
  leagueArchived?: boolean;
  onUpdated?: (league: LscLeague) => void;
}

function policyFromLeague(league: LscLeague): LscArchiveAutomationPolicy {
  return (
    league.archive_automation_policy ?? {
      mode: 'assisted_review',
      auto_run_vod_truth: false,
      auto_approve_clean_handoffs: false,
      auto_publish_clean_handoffs: false,
      publish_delay_minutes: 0,
      default_visibility: 'league_members',
      require_review_for_empty_hud: true,
      require_review_for_restart_continuation: true,
      require_review_for_side_conflict: true,
      require_review_for_low_confidence: true,
      require_review_for_manual_assignment: true,
      minimum_live_truth_confidence: 0.9,
      minimum_vod_truth_status: 'likely',
      minimum_auto_archive_score: DEFAULT_MIN_AUTO_ARCHIVE_SCORE,
      pilot_enabled: false,
    }
  );
}

const ArchiveAutomationPanel: React.FC<ArchiveAutomationPanelProps> = ({
  league,
  leagueArchived = false,
  onUpdated,
}) => {
  const initial = useMemo(() => policyFromLeague(league), [league]);
  const [draft, setDraft] = useState<LscArchiveAutomationPolicy>(initial);
  const [changeReason, setChangeReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setDraft(policyFromLeague(league));
  }, [league]);

  const autoVodTruthDisabled = draft.mode === 'manual_review';
  const autoApproveDisabled = isAutoApproveDisabled(
    draft.mode ?? 'assisted_review',
    Boolean(draft.auto_run_vod_truth)
  );
  const autoPublishDisabled = isAutoPublishDisabled(
    draft.mode ?? 'assisted_review',
    Boolean(draft.auto_run_vod_truth),
    Boolean(draft.auto_approve_clean_handoffs)
  );

  const autopilotActive = Boolean(
    draft.pilot_enabled && draft.auto_publish_clean_handoffs
  );

  const updateDraft = (patch: Partial<LscArchiveAutomationPolicy>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const handleSave = async () => {
    if (!league.id) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    const payload: UpdateLscArchiveAutomationPolicyPayload = {
      archive_automation_policy: {
        mode: draft.mode,
        auto_run_vod_truth: draft.auto_run_vod_truth,
        auto_approve_clean_handoffs: draft.auto_approve_clean_handoffs,
        auto_publish_clean_handoffs: draft.auto_publish_clean_handoffs,
        publish_delay_minutes: draft.publish_delay_minutes,
        default_visibility: draft.default_visibility,
        require_review_for_empty_hud: draft.require_review_for_empty_hud,
        require_review_for_restart_continuation: draft.require_review_for_restart_continuation,
        require_review_for_side_conflict: draft.require_review_for_side_conflict,
        require_review_for_low_confidence: draft.require_review_for_low_confidence,
        require_review_for_manual_assignment: draft.require_review_for_manual_assignment,
        minimum_live_truth_confidence: draft.minimum_live_truth_confidence,
        minimum_vod_truth_status: draft.minimum_vod_truth_status,
        minimum_auto_archive_score: draft.minimum_auto_archive_score,
        pilot_enabled: draft.pilot_enabled,
      },
      change_reason: changeReason.trim() || undefined,
    };
    try {
      const updated = await updateLscArchiveAutomationPolicy(league.id, payload);
      setSuccess('Archive automation policy updated.');
      onUpdated?.(updated);
    } catch (err) {
      setError(
        err instanceof LscApiError
          ? err.message
          : 'Could not update archive automation policy.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card space-y-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-semibold text-white">Archive automation</h2>
          <span
            className={
              autopilotActive
                ? 'inline-flex items-center rounded-full border border-emerald-700/60 bg-emerald-900/30 px-2.5 py-0.5 text-xs font-semibold text-emerald-200'
                : 'inline-flex items-center rounded-full border border-secondary-600 bg-secondary-800/60 px-2.5 py-0.5 text-xs font-semibold text-secondary-300'
            }
            data-testid="archive-automation-status"
          >
            {autopilotActive ? 'Automatic archiving on' : 'Automatic archiving off'}
          </span>
        </div>
        <p className="text-sm text-secondary-400 mt-1">
          Choose how verified league games move into the archive. Uncertain games always go to
          Needs Attention. Autopilot execution stays off until automation is enabled for this
          league.
        </p>
        <p className="text-sm text-secondary-300 mt-3">
          LSC scores each live game based on stream identity, visual evidence, matchup confidence,
          and VOD handoff quality. Strong scores can archive automatically. Uncertain scores go to
          Needs Attention.
        </p>
      </div>

      <div className="rounded-lg border border-secondary-700 bg-secondary-900/40 p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-secondary-400">
          Live Archive Confidence
        </p>
        <ul className="mt-2 space-y-1 text-sm">
          {CONFIDENCE_BANDS.map((band) => (
            <li key={band.range} className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-mono text-secondary-200">{band.range}</span>
              <span className="font-medium text-white">{band.label}</span>
              <span className="text-secondary-400">— {band.note}</span>
            </li>
          ))}
        </ul>
      </div>

      <label className="block space-y-1 text-sm">
        <span className="text-secondary-300">Mode</span>
        <select
          className={FIELD_CLASS}
          value={draft.mode ?? 'assisted_review'}
          disabled={leagueArchived || saving}
          onChange={(event) => updateDraft({ mode: event.target.value })}
        >
          {ARCHIVE_AUTOMATION_MODE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <div className="grid gap-3 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(draft.pilot_enabled)}
            disabled={leagueArchived || saving}
            onChange={(event) => updateDraft({ pilot_enabled: event.target.checked })}
          />
          <span>Enable automation for this league</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(draft.auto_run_vod_truth)}
            disabled={leagueArchived || saving || autoVodTruthDisabled}
            onChange={(event) =>
              updateDraft({ auto_run_vod_truth: event.target.checked })
            }
          />
          <span>Automatically verify completed live games</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(draft.auto_approve_clean_handoffs)}
            disabled={leagueArchived || saving || autoApproveDisabled}
            onChange={(event) =>
              updateDraft({ auto_approve_clean_handoffs: event.target.checked })
            }
          />
          <span>Automatically prepare clean games for archive</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(draft.auto_publish_clean_handoffs)}
            disabled={leagueArchived || saving || autoPublishDisabled}
            onChange={(event) =>
              updateDraft({ auto_publish_clean_handoffs: event.target.checked })
            }
          />
          <span>Automatically archive verified games</span>
        </label>
      </div>

      {draft.auto_publish_clean_handoffs && (
        <p className="text-sm text-amber-300">
          Auto-publish makes archives customer-visible after the delay. Use only after you have
          verified automatic archiving works for your league.
        </p>
      )}

      <div className="grid gap-3 text-sm">
        {REVIEW_TOGGLES.map(([field, label]) => (
          <label key={field} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(draft[field])}
              disabled={leagueArchived || saving}
              onChange={(event) => updateDraft({ [field]: event.target.checked })}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="block space-y-1 text-sm">
          <span className="text-secondary-300">Minimum score to archive automatically</span>
          <input
            className={FIELD_CLASS}
            type="number"
            min={0}
            max={100}
            step={1}
            value={draft.minimum_auto_archive_score ?? DEFAULT_MIN_AUTO_ARCHIVE_SCORE}
            disabled={leagueArchived || saving}
            onChange={(event) =>
              updateDraft({
                minimum_auto_archive_score: Math.max(
                  0,
                  Math.min(100, Math.round(Number(event.target.value) || 0))
                ),
              })
            }
          />
          <span className="block text-xs text-secondary-500">
            Games scoring below this go to Needs Attention (0–100).
          </span>
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-secondary-300">Minimum archive verification</span>
          <select
            className={FIELD_CLASS}
            value={draft.minimum_vod_truth_status ?? 'likely'}
            disabled={leagueArchived || saving}
            onChange={(event) =>
              updateDraft({ minimum_vod_truth_status: event.target.value })
            }
          >
            <option value="likely">Likely match or better</option>
            <option value="verified">Verified only</option>
          </select>
        </label>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="block space-y-1 text-sm">
          <span className="text-secondary-300">Publish delay</span>
          <select
            className={FIELD_CLASS}
            value={draft.publish_delay_minutes ?? 0}
            disabled={leagueArchived || saving}
            onChange={(event) =>
              updateDraft({ publish_delay_minutes: Number(event.target.value) })
            }
          >
            {DELAY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-secondary-300">Default visibility</span>
          <select
            className={FIELD_CLASS}
            value={draft.default_visibility ?? 'league_members'}
            disabled={leagueArchived || saving}
            onChange={(event) => updateDraft({ default_visibility: event.target.value })}
          >
            {VISIBILITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block space-y-1 text-sm">
        <span className="text-secondary-300">Change reason (optional)</span>
        <input
          className={FIELD_CLASS}
          value={changeReason}
          disabled={leagueArchived || saving}
          onChange={(event) => setChangeReason(event.target.value)}
          placeholder="Why are you changing automation policy?"
        />
      </label>

      {draft.policy_version != null && (
        <p className="text-xs text-secondary-500">Policy version {draft.policy_version}</p>
      )}

      {error && <ErrorBanner message={error} />}
      {success && <p className="text-sm text-emerald-400">{success}</p>}

      <button
        type="button"
        className="btn-primary"
        disabled={leagueArchived || saving}
        onClick={() => void handleSave()}
      >
        {saving ? 'Saving…' : 'Save archive automation policy'}
      </button>
    </div>
  );
};

export default ArchiveAutomationPanel;
